import { findFacilityProcedureGroups } from './find-facility-procedure-groups.service';

import {
  findProceduresBySlugs,
  findProceduresPricing,
} from '@/app/(main)/(pages)/_services/search-api/search-api.service';
import {
  SearchApiFacilityExam,
  SearchApiPartnerProcedureGroup,
  SearchApiProcedure,
  SearchApiProcedurePricing,
} from '@/app/(main)/(pages)/_types/search-api-response.type';
import { getLogger } from '@/logger/logger';

export interface NotOfferedExam {
  id: string;
  name: string;
}

export interface LaboratoryExamsCoverage {
  offered: SearchApiFacilityExam[];
  notOffered: NotOfferedExam[];
  matched: number;
  total: number;
  isFullCoverage: boolean;
}

function offeredProcedureIds(
  groups: SearchApiPartnerProcedureGroup[],
  facilityId: string,
): Set<string> {
  const offered = groups
    .filter((group) => (group.options ?? []).some((option) => option.partnerFacilityId === facilityId))
    .map((group) => group.procedure.id);

  return new Set(offered);
}

function toFacilityExam(
  procedure: SearchApiProcedure,
  pricing: SearchApiProcedurePricing | null,
): SearchApiFacilityExam {
  const priced = pricing?.procedures.find((item) => item.procedureId === procedure.id);

  return {
    id: procedure.id,
    name: procedure.name,
    code: procedure.code,
    tussCode: procedure.tussCode,
    price: priced?.price ?? 0,
    promotion: priced?.promotion ?? null,
  };
}

export interface GetLaboratoryExamsCoverageParams {
  facilityId: string;
  examSlugs: string[];
}

export async function getLaboratoryExamsCoverage({
  facilityId,
  examSlugs,
}: GetLaboratoryExamsCoverageParams): Promise<LaboratoryExamsCoverage | null> {
  const logger = getLogger('getLaboratoryExamsCoverage');

  if (!facilityId || examSlugs.length === 0) {
    return null;
  }

  try {
    const procedures = await findProceduresBySlugs(examSlugs);

    if (procedures.length === 0) {
      return null;
    }

    const { partnerId, groups } = await findFacilityProcedureGroups({
      facilityId,
      procedureIds: procedures.map((procedure) => procedure.id),
    });

    if (!partnerId) {
      return null;
    }

    const offeredIds = offeredProcedureIds(groups, facilityId);
    const pricing = offeredIds.size > 0
      ? await findProceduresPricing(facilityId, [...offeredIds])
      : null;

    const offered = procedures
      .filter((procedure) => offeredIds.has(procedure.id))
      .map((procedure) => toFacilityExam(procedure, pricing));

    const notOffered = procedures
      .filter((procedure) => !offeredIds.has(procedure.id))
      .map((procedure) => ({ id: procedure.id, name: procedure.name }));

    return {
      offered,
      notOffered,
      matched: offered.length,
      total: procedures.length,
      isFullCoverage: notOffered.length === 0,
    };
  } catch (error) {
    logger.error('Failed to resolve laboratory exams coverage', { facilityId, examSlugs, error });

    throw error;
  }
}
